/**
 * Configuration for zip code validations by country
 */

import * as Yup from 'yup';

import { COUNTRY_ZIPCODE_CONFIG } from '@/constants/countries';

export const ZIPCODE_CONFIG = {
  MEX: {
    length: COUNTRY_ZIPCODE_CONFIG.MEX.length,
    regex: COUNTRY_ZIPCODE_CONFIG.MEX.pattern,
    message: COUNTRY_ZIPCODE_CONFIG.MEX.errorMessage,
    required: COUNTRY_ZIPCODE_CONFIG.MEX.required,
    label: 'Código Postal',
  },
  CHL: {
    length: COUNTRY_ZIPCODE_CONFIG.CHL.length,
    regex: COUNTRY_ZIPCODE_CONFIG.CHL.pattern,
    message: COUNTRY_ZIPCODE_CONFIG.CHL.errorMessage,
    required: COUNTRY_ZIPCODE_CONFIG.CHL.required,
    label: 'Código Postal',
  },
  COL: {
    length: COUNTRY_ZIPCODE_CONFIG.COL.length,
    regex: COUNTRY_ZIPCODE_CONFIG.COL.pattern,
    message: COUNTRY_ZIPCODE_CONFIG.COL.errorMessage,
    required: COUNTRY_ZIPCODE_CONFIG.COL.required,
    label: 'Código Postal',
  },
  PER: {
    length: COUNTRY_ZIPCODE_CONFIG.PER.length,
    regex: COUNTRY_ZIPCODE_CONFIG.PER.pattern,
    message: COUNTRY_ZIPCODE_CONFIG.PER.errorMessage,
    required: COUNTRY_ZIPCODE_CONFIG.PER.required,
    label: 'Código Postal',
  },
} as const;

export type CountryCode = keyof typeof ZIPCODE_CONFIG;

/**
 * Gets the zip code configuration for a specific country
 * @param countryCode - Country code (MEX, CHL, COL, PER)
 * @returns Zip code configuration for the country
 */
export const getZipcodeConfig = (countryCode?: string) => {
  const code = (countryCode || 'MEX') as CountryCode;
  return ZIPCODE_CONFIG[code] || ZIPCODE_CONFIG.MEX;
};

/**
 * Validates if a zip code is valid for a specific country
 * @param zipcode - Zip code to validate
 * @param countryCode - Country code
 * @returns true if valid, false otherwise
 */
export const isValidZipcode = (zipcode: string, countryCode?: string): boolean => {
  if (!zipcode) return false;
  const config = getZipcodeConfig(countryCode);
  return config.regex.test(zipcode);
};

/**
 * Gets the maximum length of the zip code for a specific country
 * @param countryCode - Country code
 * @returns Maximum length of the zip code
 */
export const getZipcodeMaxLength = (countryCode?: string): number => {
  const config = getZipcodeConfig(countryCode);
  return config.length;
};

/**
 * Validates if it only contains numbers
 * @param value - Value to validate
 * @returns true if it only contains numbers, false otherwise
 */
export const isNumericOnly = (value: string): boolean => {
  return /^\d*$/.test(value);
};

/**
 * Reusable Yup validation chain for zipcode based on the selected country.
 * By default it reads the field named "country" from the same object.
 */
export const createZipcodeValidation = (countryField: string = 'country') => {
  return Yup.string()
    .test('is-numeric', 'El código postal solo debe contener números', (value) => {
      return !value || isNumericOnly(value);
    })
    .when(countryField, (country, schema) => {
      const countryCode = Array.isArray(country) ? country[0] : country;
      const config = getZipcodeConfig(countryCode as string);

      const validationSchema = config.required
        ? schema.required('Este campo es requerido')
        : schema.optional();

      return validationSchema.test('zipcode-format', config.message, (value) => {
        if (!config.required && !value) return true;
        if (!value) return false;
        return config.regex.test(value);
      });
    });
};

/**
 * Handler for the onKeyPress event that only allows numbers
 * @param e - Keyboard event
 */
export const handleNumericKeyPress = (e: React.KeyboardEvent<HTMLInputElement>) => {
  const allowedKeys = [
    'Backspace',
    'Delete',
    'Tab',
    'Enter',
    'ArrowLeft',
    'ArrowRight',
    'ArrowUp',
    'ArrowDown',
    'Home',
    'End',
  ];
  if (!/[0-9]/.test(e.key) && !allowedKeys.includes(e.key)) {
    e.preventDefault();
  }
};
/**
 * Common props for zip code inputs
 */
export const getZipcodeInputProps = (countryCode?: string) => {
  const maxLength = getZipcodeMaxLength(countryCode);

  return {
    inputProps: {
      maxLength,
      inputMode: 'numeric' as const,
      pattern: '[0-9]*',
    },
    onKeyPress: handleNumericKeyPress,
  };
};
